import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { LookUpService } from '../../../services/lookupservice';
import { MMSMenu } from '../../../interfaces/mmsMenu';
import { RouterLinkActive } from '@angular/router';
import { NavigationService } from '../../../services/navigationService';
import { RoleService } from '../../../services/role.service';
import { filter, Subject, take, takeUntil } from 'rxjs';

@Component({
  selector: 'app-side-menu',
  imports: [RouterLink, RouterLinkActive,MatIconModule, CommonModule],
  templateUrl: './side-menu.component.html',
  styleUrl: './side-menu.component.scss'
})
export class SideMenuComponent implements OnInit, OnDestroy {

  @Output() closeSidenav = new EventEmitter<void>();
  menus: MMSMenu[] = [];
  role: string = "";
  private destroy$ = new Subject<void>();

  constructor(private lookUp: LookUpService, private navService: NavigationService,
    private router: Router, private roleService: RoleService
  ) {

  }

  ngOnInit(): void {
    this.roleService.roles$
    .pipe(take(1), takeUntil(this.destroy$))
    .subscribe(roles => {
      let rolesString = roles.join(',') || 'NoRole';
      this.lookUp.getMmsMenus(rolesString).subscribe({
        next: (data) => {
          this.menus = data.filter(m => m.status);
          this.navService.setMenus(this.menus);
          console.log(`Menus for ${roles}`, this.menus);
          this.syncTitleWithCurrentUrl();
        }
      });
    });

    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.syncTitleWithCurrentUrl();
    });
  }

  private syncTitleWithCurrentUrl(): void {
    const currentUrl = this.router.url.split(/[?#]/, 1)[0].replace(/\/+$/, '') || '/';

    // Home page — clear the title
    if (currentUrl === '/' || currentUrl === '') {
      this.navService.setTitle('');
      return;
    }

    // Match route boundaries so `/site` does not also match `/sitedatacapture`.
    // Prefer the most specific route if menu routes overlap.
    const match = [...this.menus]
      .sort((a, b) => (b.routeLink?.length ?? 0) - (a.routeLink?.length ?? 0))
      .find(m => {
        const route = m.routeLink?.startsWith('/') ? m.routeLink : `/${m.routeLink}`;
        const normalizedRoute = route.replace(/\/+$/, '') || '/';
        return currentUrl === normalizedRoute || currentUrl.startsWith(`${normalizedRoute}/`);
      });

    if (match) {
      this.navService.setTitle(match.screenName ?? '');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  iconMap: any = {
    ADSchedule: 'schedule',
    ORFSchedule: 'calendar_month',
    StrategicPartnershipSchedule: 'view_list',
    AddTruckType: 'local_shipping',
    DailyDosing: 'science',
    DosingRates: 'speed',
    DataOverride: 'sync_problem',
    GeneratorInfo: 'fact_check',
    Haulers: 'local_shipping',
    Sites: 'location_on',
    Markets: 'store',
    LoadProjections: 'show_chart',
    VGSGasInput: 'energy_savings_leaf',
    ORAOutbound: 'north_east',
    DosingBalance: 'balance',
    AGSitesDataCapture: 'dataset',
    InvoiceProcess: 'receipt_long',
    PlantUtilization: 'engineering',
    SiteLockCodes: 'lock',
    ToteTransaction: 'compare_arrows',
    SiteDataCapture: 'cloud_upload',
    LabDataCapture: 'biotech',
    DepackSchedule: 'schedule'
  };

  getIcon(menuName: string | null): string {
    if (!menuName) return 'menu';
    const key = menuName.replace(/\s+/g, '');  
    return this.iconMap[key] || 'menu';
  }

  selectMenu(name: string) {
    this.navService.setTitle(name);
    this.closeSidenav.emit();
  }

}
